"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { Copy, Ellipsis, ImageDown, Link2 } from "lucide-react";
import toast from "react-hot-toast";

import { buildBlockShareUrl } from "@/lib/reports/block-presenters";
import { queryResultToCsv } from "@/lib/reports/export/csv";
import type { QueryResult } from "@/lib/reports/adapters/types";

import styles from "./ReportBlockExportMenu.module.css";

const MENU_WIDTH = 236;
const MENU_GUTTER = 10;

interface ReportBlockExportMenuProps {
  reportId?: string;
  blockId: string;
  title: string;
  result?: QueryResult;
  imageTargetRef: React.RefObject<HTMLElement | null>;
  shareUrl?: string | (() => string);
  urlLabel?: string;
  urlSuccessMessage?: string;
}

export function ReportBlockExportMenu({
  reportId,
  blockId,
  title,
  result,
  imageTargetRef,
  shareUrl,
  urlLabel = "Copy section URL",
  urlSuccessMessage = "Section URL copied",
}: ReportBlockExportMenuProps) {
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const hasRows = Boolean(result?.rows?.length);

  const updateMenuPosition = React.useCallback(() => {
    const button = buttonRef.current;
    if (!button || typeof window === "undefined") return;
    const rect = button.getBoundingClientRect();
    const left = Math.max(
      MENU_GUTTER,
      Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - MENU_GUTTER),
    );
    const top = Math.min(rect.bottom + 6, window.innerHeight - 132);
    setMenuPosition({ top: Math.max(MENU_GUTTER, top), left });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  async function copyUrl() {
    try {
      const url =
        typeof shareUrl === "function"
          ? shareUrl()
          : shareUrl ?? (reportId ? buildBlockShareUrl(reportId, blockId, window.location.origin) : "");
      if (!url) throw new Error("no share URL is available");
      await writeClipboard(url);
      toast.success(urlSuccessMessage);
    } catch (err) {
      toast.error(`Couldn't copy the URL — ${(err as Error).message}`);
    } finally {
      setOpen(false);
    }
  }

  async function downloadImage() {
    setOpen(false);
    await nextFrame();
    const target = imageTargetRef.current;
    if (!target) {
      toast.error("This section is still rendering.");
      return;
    }
    const restoreCaptureStyles = prepareForImageCapture(target);
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(target, {
        backgroundColor: "#ffffff",
        cacheBust: true,
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          return !node.matches("[data-report-export-trigger], [data-drag-handle]");
        },
        pixelRatio: Math.min(window.devicePixelRatio || 2, 2),
        skipFonts: true,
      });
      downloadDataUrl(dataUrl, `${slugify(title || "report-section")}.png`);
      toast.success("Image downloaded");
    } catch (err) {
      toast.error(`Couldn't render the image — ${(err as Error).message}`);
    } finally {
      restoreCaptureStyles();
    }
  }

  async function copyCsv() {
    try {
      if (!result || result.rows.length === 0) {
        toast.error("This section has no rows to copy.");
        return;
      }
      await writeClipboard(queryResultToCsv(result, { formatValues: false }));
      toast.success("CSV copied");
    } catch (err) {
      toast.error(`Couldn't copy the CSV — ${(err as Error).message}`);
    } finally {
      setOpen(false);
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.trigger}
        aria-label={`Export ${title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-open={open}
        data-report-export-trigger={blockId}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <Ellipsis size={16} aria-hidden="true" />
      </button>
      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              className={styles.menu}
              role="menu"
              aria-label={`Export options for ${title}`}
              style={{ top: menuPosition.top, left: menuPosition.left }}
              data-report-export-menu={blockId}
              onClick={(event) => event.stopPropagation()}
            >
              <button type="button" className={styles.item} role="menuitem" onClick={copyUrl}>
                <Link2 size={15} aria-hidden="true" />
                <span>{urlLabel}</span>
              </button>
              <button
                type="button"
                className={styles.item}
                role="menuitem"
                onClick={downloadImage}
                disabled={!result}
              >
                <ImageDown size={15} aria-hidden="true" />
                <span>Download image</span>
              </button>
              <button
                type="button"
                className={styles.item}
                role="menuitem"
                onClick={copyCsv}
                disabled={!hasRows}
              >
                <Copy size={15} aria-hidden="true" />
                <span>Copy data as CSV</span>
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) throw new Error("clipboard access was blocked");
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return slug || "report-section";
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

function prepareForImageCapture(target: HTMLElement): () => void {
  const touched: Array<{
    el: HTMLElement;
    overflow: string;
    textOverflow: string;
    maxWidth: string;
    minWidth: string;
    flexShrink: string;
  }> = [];

  target.querySelectorAll<HTMLElement>("[data-kpi-value]").forEach((el) => {
    touched.push({
      el,
      overflow: el.style.overflow,
      textOverflow: el.style.textOverflow,
      maxWidth: el.style.maxWidth,
      minWidth: el.style.minWidth,
      flexShrink: el.style.flexShrink,
    });
    el.style.overflow = "visible";
    el.style.textOverflow = "clip";
    el.style.maxWidth = "none";
    el.style.minWidth = "max-content";
    el.style.flexShrink = "0";
  });

  return () => {
    touched.forEach(({ el, overflow, textOverflow, maxWidth, minWidth, flexShrink }) => {
      el.style.overflow = overflow;
      el.style.textOverflow = textOverflow;
      el.style.maxWidth = maxWidth;
      el.style.minWidth = minWidth;
      el.style.flexShrink = flexShrink;
    });
  };
}
